import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Portal,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import CampaignHubEngagement from './CampaignHubEngagement';
import DefaultTableLayout from '../components/tables/default/DefaultTableLayout';
import { DEFAULT_TABLES } from '../components/tables/shared/defaultTableRegistry';
import { TableCopyButton, TableEmptyState, TableHeader, TableLoadingState, TablePagination, TableSearch, TableStatusBadge, TableSurface } from '../components/tables/shared/TablePrimitives';

// Keep the registry allowlisted: table keys select known React views, never Mongo collections.
const OPERATORS_BY_CATEGORY = {
  string: [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  number: [
    { value: 'equals', label: '=' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '>=' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '<=' },
  ],
  date: [
    { value: 'equals', label: 'on date' },
    { value: 'before', label: 'before date' },
    { value: 'after', label: 'after date' },
  ],
};

/** Purpose: Perform get category for field type for the default data module. Why: Callers need a consistent result from this shared operation. */
const getCategoryForFieldType = (type = 'string') => {
  if (type === 'number') return 'number';
  if (type === 'date') return 'date';
  return 'string';
};

/** Purpose: Perform evaluate rule for the default data module. Why: Callers need a consistent result from this shared operation. */
const evaluateRule = (rawVal, operator, filterVal, category) => {
  const isValEmpty = rawVal === null || rawVal === undefined || rawVal === '';
  if (operator === 'is_empty') return isValEmpty;
  if (operator === 'is_not_empty') return !isValEmpty;

  if (isValEmpty) return false;

  if (category === 'number') {
    const num = Number(rawVal);
    const target = Number(filterVal);
    if (Number.isNaN(num) || Number.isNaN(target)) return false;
    if (operator === 'equals') return num === target;
    if (operator === 'gt') return num > target;
    if (operator === 'gte') return num >= target;
    if (operator === 'lt') return num < target;
    if (operator === 'lte') return num <= target;
    return true;
  }

  if (category === 'date') {
    const dVal = new Date(rawVal).getTime();
    const dTarget = new Date(filterVal).getTime();
    if (Number.isNaN(dVal) || Number.isNaN(dTarget)) return false;
    if (operator === 'equals') {
      try {
        return new Date(rawVal).toISOString().slice(0, 10) === new Date(filterVal).toISOString().slice(0, 10);
      } catch {
        return false;
      }
    }
    if (operator === 'before') return dVal < dTarget;
    if (operator === 'after') return dVal > dTarget;
    return true;
  }

  const strVal = String(rawVal).toLowerCase();
  const strTarget = String(filterVal || '').toLowerCase();
  if (operator === 'contains') return strVal.includes(strTarget);
  if (operator === 'equals') return strVal === strTarget;
  if (operator === 'not_equals') return strVal !== strTarget;
  if (operator === 'starts_with') return strVal.startsWith(strTarget);
  return true;
};

const DEFAULT_DATA_FIELDS = [
  { key: 'channel', label: 'Channel', type: 'string' },
  { key: 'status', label: 'Status', type: 'string' },
  { key: 'owner', label: 'Owner', type: 'string' },
  { key: 'messageCount', label: 'Messages', type: 'number' },
  { key: 'flowId', label: 'Flow', type: 'string' },
  { key: 'userUid', label: 'User UID', type: 'string' },
  { key: 'sessionId', label: 'Session ID', type: 'string' },
  { key: 'conversationId', label: 'Conversation ID', type: 'string' },
  { key: 'lastActivityAt', label: 'Last Activity', type: 'date' },
];

/** Purpose: Perform format date for the default data module. Why: Callers need a consistent result from this shared operation. */
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

/** Purpose: expose built-in reference data in a read-only browser. Why: flow builders need to inspect platform data fields without risking changes to shared schemas. */
export default function DefaultData() {
  const { selectedBot, selectedEnv, selectedBotDetails, isAdmin, isBotAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTable = searchParams.get('table') || 'default_data';
  const workspaceRoles = Array.isArray(selectedBotDetails?.workspaceRoles) && selectedBotDetails.workspaceRoles.length
    ? selectedBotDetails.workspaceRoles : [selectedBotDetails?.workspaceRole || ''];
  const canViewNotifications = Boolean(isAdmin?.() || isBotAdmin?.(selectedBot) || workspaceRoles.includes('developer'));
  const availableTables = Object.values(DEFAULT_TABLES).filter((table) => table.key !== 'notifications' || canViewNotifications);
  const knownTable = availableTables.some((table) => table.key === selectedTable);
  const showNotifications = selectedTable === 'notifications' && canViewNotifications;
  const [headerHost, setHeaderHost] = useState(() => (
    typeof document !== 'undefined' ? document.getElementById('default-data-header-actions') : null
  ));

  useEffect(() => {
    if (!headerHost && typeof document !== 'undefined') {
      const element = document.getElementById('default-data-header-actions');
      if (element) setHeaderHost(element);
    }
  }, [headerHost]);

  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 50 });
  const [pageSize, setPageSize] = useState(50);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [conditions, setConditions] = useState([]);
  const [matchMode, setMatchMode] = useState('and');
  const [showConditionsPanel, setShowConditionsPanel] = useState(false);
  const [newCondField, setNewCondField] = useState('channel');
  const [newCondOperator, setNewCondOperator] = useState('equals');
  const [newCondValue, setNewCondValue] = useState('');
  const [sortField, setSortField] = useState('lastActivityAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedValue, setCopiedValue] = useState('');
  const recordsRequestVersion = useRef(0);

  useEffect(() => {
    if (!knownTable) {
      const next = new URLSearchParams(searchParams);
      next.set('table', 'default_data');
      setSearchParams(next, { replace: true });
    }
  }, [knownTable, searchParams, setSearchParams]);

  /** Purpose: Load load records for the default data module. Why: The selected view needs current data to render or continue processing. */
  const loadRecords = useCallback(async (page = 1) => {
    const requestVersion = ++recordsRequestVersion.current;
    if (!selectedBot) {
      setRecords([]);
      setLoading(false);
      setError('Select a bot to view platform data.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/v1/analytics/platform-data', {
        params: {
          botId: selectedBot,
          envName: selectedEnv || 'dev',
          page,
          limit: pageSize,
          search: search || undefined,
          channel: channelFilter !== 'all' ? channelFilter : undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          owner: ownerFilter !== 'all' ? ownerFilter : undefined,
          conditions: conditions.length > 0 ? JSON.stringify(conditions) : undefined,
          matchMode,
          sortBy: sortField,
          sortOrder,
        },
      });
      if (requestVersion !== recordsRequestVersion.current) return;
      setRecords(Array.isArray(response.data?.records) ? response.data.records : []);
      setPagination(response.data?.pagination || { page, pages: 1, total: 0, limit: pageSize });
    } catch (requestError) {
      if (requestVersion !== recordsRequestVersion.current) return;
      setRecords([]);
      setError(requestError.response?.data?.error || 'Failed to load platform data.');
    } finally {
      if (requestVersion === recordsRequestVersion.current) setLoading(false);
    }
  }, [channelFilter, conditions, matchMode, ownerFilter, pageSize, search, selectedBot, selectedEnv, sortField, sortOrder, statusFilter]);

  useEffect(() => {
    if (selectedTable === 'default_data') loadRecords(1);
  }, [loadRecords, selectedTable]);

  /** Purpose: Handle apply search in the default data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const applySearch = (event) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
  };

  /** Purpose: Handle handle sort in the default data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder(field === 'lastActivityAt' || field === 'messageCount' ? 'desc' : 'asc');
    }
  };

  /** Purpose: Handle handle add condition in the default data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleAddCondition = () => {
    const fieldKey = newCondField || 'channel';
    /** Purpose: Resolve field def for the default data module. Why: The surrounding operation uses this value to make its decision. */
    const fieldDef = DEFAULT_DATA_FIELDS.find((f) => f.key === fieldKey);
    const category = getCategoryForFieldType(fieldDef?.type);
    const isValLess = ['is_empty', 'is_not_empty'].includes(newCondOperator);
    if (!isValLess && !String(newCondValue).trim()) return;

    setConditions((prev) => [
      ...prev,
      {
        id: `cond-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        field: fieldKey,
        fieldLabel: fieldDef?.label || fieldKey,
        fieldType: fieldDef?.type || 'string',
        category,
        operator: newCondOperator,
        value: newCondValue,
      },
    ]);
    setNewCondValue('');
  };

  /** Purpose: Handle handle update condition in the default data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleUpdateCondition = (condId, updates) => {
    setConditions((prev) =>
      prev.map((c) => {
        if (c.id !== condId) return c;
        const next = { ...c, ...updates };
        if (updates.field && updates.field !== c.field) {
          /** Purpose: Resolve field def for the default data module. Why: The surrounding operation uses this value to make its decision. */
          const fieldDef = DEFAULT_DATA_FIELDS.find((f) => f.key === updates.field);
          next.fieldType = fieldDef?.type || 'string';
          next.fieldLabel = fieldDef?.label || updates.field;
          next.category = getCategoryForFieldType(next.fieldType);
          const ops = OPERATORS_BY_CATEGORY[next.category] || OPERATORS_BY_CATEGORY.string;
          next.operator = ops[0]?.value || 'equals';
        }
        return next;
      })
    );
  };

  /** Purpose: Handle handle remove condition in the default data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleRemoveCondition = (condId) => {
    setConditions((prev) => prev.filter((c) => c.id !== condId));
  };

  const hasActiveFilters = Boolean(
    search || channelFilter !== 'all' || statusFilter !== 'all' || ownerFilter !== 'all' || conditions.length > 0
  );

  /** Purpose: Handle reset filters in the default data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const resetFilters = () => {
    setSearchDraft('');
    setSearch('');
    setChannelFilter('all');
    setStatusFilter('all');
    setOwnerFilter('all');
    setConditions([]);
    setMatchMode('and');
  };

  /** Purpose: Perform sorted records for the default data module. Why: Callers need a consistent result from this shared operation. */
  const sortedRecords = useMemo(() => {
    let result = records;

    if (conditions.length > 0) {
      result = result.filter((rec) => {
        /** Purpose: Resolve eval cond for the default data module. Why: The surrounding operation uses this value to make its decision. */
        const evalCond = (c) => {
          let rawVal = rec[c.field];
          if (c.field === 'userUid') {
            rawVal = rec.userUid || '';
          }
          /** Purpose: Resolve field def for the default data module. Why: The surrounding operation uses this value to make its decision. */
          const fieldDef = DEFAULT_DATA_FIELDS.find((f) => f.key === c.field);
          const category = getCategoryForFieldType(fieldDef?.type);
          return evaluateRule(rawVal, c.operator, c.value, category);
        };
        return matchMode === 'or' ? conditions.some(evalCond) : conditions.every(evalCond);
      });
    }

    if (!sortField) return result;
    return [...result].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'userUid') {
        aVal = a.userUid || '';
        bVal = b.userUid || '';
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (sortField === 'lastActivityAt' || sortField === 'createdAt') {
        const aTime = aVal ? new Date(aVal).getTime() : 0;
        const bTime = bVal ? new Date(bVal).getTime() : 0;
        return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
      }
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      return sortOrder === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [records, conditions, matchMode, sortField, sortOrder]);

  /** Purpose: Handle copy id in the default data module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const copyId = async (value) => {
    const text = String(value || '');
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopiedValue(text);
    window.setTimeout(() => setCopiedValue((current) => current === text ? '' : current), 1400);
  };

  /** Purpose: Perform render copyable id for the default data module. Why: Callers need a consistent result from this shared operation. */
  const renderCopyableId = (value) => {
    const text = String(value || '');
    if (!text) return '—';
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, maxWidth: 210 }}>
        <Typography
          component="span"
          title={text}
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 13 }}
        >
          {text}
        </Typography>
        <Tooltip title={copiedValue === text ? 'Copied full ID' : 'Copy full ID'}>
          <TableCopyButton
            label="Copy full ID"
            onClick={() => copyId(text)}
            sx={{ minWidth: 28, width: 28, height: 28, p: 0, borderRadius: 1.5 }}
          >
            {copiedValue === text ? <CheckOutlinedIcon sx={{ fontSize: 16 }} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />}
          </TableCopyButton>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <DefaultTableLayout tableKey={knownTable ? selectedTable : 'default_data'}>
      <Portal container={headerHost} disablePortal={!headerHost}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexShrink: 0, pt: 0.75, pb: 0.25 }}>
          <FormControl size="small" sx={{ minWidth: 190, flexShrink: 0 }}>
            <InputLabel id="system-table-select-label">Select table</InputLabel>
            <Select
              labelId="system-table-select-label"
              label="Select table"
              value={knownTable ? selectedTable : 'default_data'}
              onChange={(event) => {
                const next = new URLSearchParams(searchParams);
                next.set('table', event.target.value);
                setSearchParams(next);
              }}
              sx={{ height: 38, borderRadius: '10px', fontSize: '0.8125rem' }}
            >
              {availableTables.map((table) => <MenuItem key={table.key} value={table.key}>{table.label}</MenuItem>)}
            </Select>
          </FormControl>
          {showNotifications ? null : <>
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: 100, sm: 115 },
              '& .MuiInputLabel-root': {
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.2,
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)',
                  /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#0f172a' : '#ffffff',
                  px: 0.5,
                  borderRadius: '4px',
                },
              },
            }}
          >
            <InputLabel id="channel-filter-label" sx={{ fontSize: '0.8125rem' }}>Channel</InputLabel>
            <Select
              labelId="channel-filter-label"
              label="Channel"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              sx={{
                borderRadius: '10px',
                height: 38,
                /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                fontSize: '0.8125rem',
              }}
            >
              <MenuItem value="all">All Channels</MenuItem>
              <MenuItem value="web">Web</MenuItem>
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
              <MenuItem value="voice">Voice</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              minWidth: { xs: 95, sm: 110 },
              '& .MuiInputLabel-root': {
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.2,
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)',
                  /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#0f172a' : '#ffffff',
                  px: 0.5,
                  borderRadius: '4px',
                },
              },
            }}
          >
            <InputLabel id="status-filter-label" sx={{ fontSize: '0.8125rem' }}>Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{
                borderRadius: '10px',
                height: 38,
                /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                fontSize: '0.8125rem',
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="escalated">Escalated</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              minWidth: { xs: 95, sm: 105 },
              '& .MuiInputLabel-root': {
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.2,
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)',
                  /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#0f172a' : '#ffffff',
                  px: 0.5,
                  borderRadius: '4px',
                },
              },
            }}
          >
            <InputLabel id="owner-filter-label" sx={{ fontSize: '0.8125rem' }}>Owner</InputLabel>
            <Select
              labelId="owner-filter-label"
              label="Owner"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              sx={{
                borderRadius: '10px',
                height: 38,
                /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                fontSize: '0.8125rem',
              }}
            >
              <MenuItem value="all">All Owners</MenuItem>
              <MenuItem value="bot">Bot</MenuItem>
              <MenuItem value="agent">Agent</MenuItem>
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => loadRecords(pagination.page)}
            sx={{
              borderRadius: '10px',
              height: 38,
              textTransform: 'none',
              fontWeight: 650,
              fontSize: '0.8125rem',
              whiteSpace: 'nowrap',
              px: 1.75,
              '& .MuiButton-startIcon': {
                marginLeft: 0,
                mr: 0.75,
              },
            }}
          >
            Refresh
          </Button>
          </>}
        </Stack>
      </Portal>

      {showNotifications ? (
        <CampaignHubEngagement section="notifications" embedded />
      ) : (
      <>

      <TableSurface sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack component="form" onSubmit={applySearch} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <TableSearch
              fullWidth
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onSubmit={applySearch}
              label="Search platform data"
              placeholder="Search user UID, session ID, conversation ID, or name"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  height: 38,
                  fontSize: '0.8125rem',
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{
                px: 2.5,
                height: 38,
                whiteSpace: 'nowrap',
                fontWeight: 650,
                borderRadius: '10px',
                textTransform: 'none',
                fontSize: '0.8125rem',
                boxShadow: 'none',
              }}
            >
              Search
            </Button>
            
            <Button
              size="small"
              variant={conditions.length > 0 ? 'contained' : 'outlined'}
              startIcon={<TuneIcon sx={{ fontSize: 17 }} />}
              endIcon={
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 18,
                    transform: showConditionsPanel ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                />
              }
              onClick={() => setShowConditionsPanel((prev) => !prev)}
              sx={{
                borderRadius: '10px',
                height: 38,
                textTransform: 'none',
                fontWeight: 650,
                fontSize: '0.8125rem',
                whiteSpace: 'nowrap',
                px: 2,
                '& .MuiButton-startIcon': {
                  marginLeft: 0,
                  mr: 0.85,
                },
                '& .MuiButton-endIcon': {
                  marginRight: 0,
                  ml: 0.85,
                },
                ...(conditions.length === 0
                  ? {
                      /** Purpose: Derive the border color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : '#cbd5e1',
                      color: 'text.primary',
                      /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.5)' : '#ffffff',
                      '&:hover': {
                        borderColor: 'primary.main',
                        /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                      },
                    }
                  : {
                      boxShadow: 'none',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                      },
                    }),
              }}
            >
              Conditions
              {conditions.length > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.85,
                    px: 0.75,
                    py: 0.1,
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    borderRadius: '9999px',
                    bgcolor: 'primary.contrastText',
                    color: 'primary.main',
                    lineHeight: 1.4,
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  {conditions.length}
                </Box>
              )}
            </Button>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                height: 38,
                px: 1.5,
                borderRadius: '10px',
                border: '1px solid',
                /** Purpose: Derive the border color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.5)' : '#f8fafc',
                color: 'text.secondary',
                fontSize: '0.8125rem',
                whiteSpace: 'nowrap',
              }}
            >
              <Box component="span" sx={{ fontWeight: 750, color: 'text.primary' }}>
                {pagination.total.toLocaleString()}
              </Box>
              <span>records</span>
            </Box>
          </Stack>

          {/* Multi-Condition Filter Panel */}
          <Collapse in={showConditionsPanel}>
            <Box
              sx={{
                p: { xs: 1.75, sm: 2.25 },
                borderRadius: '14px',
                border: '1px solid',
                /** Purpose: Derive the border color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
                /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.75)' : '#f8fafc',
                /** Purpose: Derive the box shadow style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                boxShadow: (theme) => theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.03)',
              }}
            >
              <Stack spacing={2}>
                {/* Panel Header */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                  sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '8px',
                          /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.2)' : '#dbeafe',
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <TuneIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 750, fontSize: '0.875rem' }}>
                        Multiple Filter Conditions
                      </Typography>
                    </Stack>

                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={matchMode}
                      onChange={(e, val) => {
                        if (val) setMatchMode(val);
                      }}
                      sx={{
                        height: 30,
                        borderRadius: '8px',
                        /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                        border: '1px solid',
                        borderColor: 'divider',
                        p: '2px',
                        '& .MuiToggleButton-root': {
                          border: 'none',
                          borderRadius: '6px',
                          textTransform: 'none',
                          px: 1.5,
                          py: 0,
                          fontSize: '0.75rem',
                          fontWeight: 650,
                          color: 'text.secondary',
                          '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: '#ffffff',
                            fontWeight: 750,
                            '&:hover': { bgcolor: 'primary.dark' },
                          },
                        },
                      }}
                    >
                      <ToggleButton value="and">Match ALL (AND)</ToggleButton>
                      <ToggleButton value="or">Match ANY (OR)</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {conditions.length > 0 && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setConditions([])}
                        sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 650 }}
                      >
                        Clear all ({conditions.length})
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowConditionsPanel(false)}
                      startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 650,
                        height: 30,
                      }}
                    >
                      Done
                    </Button>
                  </Stack>
                </Stack>

                {/* Existing Conditions List */}
                {conditions.length > 0 && (
                  <Stack spacing={1}>
                    {conditions.map((c, idx) => {
                      /** Purpose: Resolve field def for the default data module. Why: The surrounding operation uses this value to make its decision. */
                      const fieldDef = DEFAULT_DATA_FIELDS.find((f) => f.key === c.field);
                      const category = getCategoryForFieldType(fieldDef?.type);
                      const isValHidden = ['is_empty', 'is_not_empty'].includes(c.operator);

                      return (
                        <Stack
                          key={c.id}
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', md: 'center' }}
                          sx={{
                            p: 1.25,
                            borderRadius: '10px',
                            /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#ffffff',
                            border: '1px solid',
                            /** Purpose: Derive the border color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
                            /** Purpose: Derive the box shadow style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                            boxShadow: (theme) => theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.02)',
                          }}
                        >
                          <Chip
                            label={idx === 0 ? 'Where' : matchMode.toUpperCase()}
                            size="small"
                            color={idx === 0 ? 'default' : 'primary'}
                            sx={{
                              fontWeight: 800,
                              minWidth: 64,
                              textTransform: 'uppercase',
                              fontSize: '0.72rem',
                              height: 28,
                              borderRadius: '6px',
                            }}
                          />

                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select
                              value={c.field}
                              onChange={(e) => handleUpdateCondition(c.id, { field: e.target.value })}
                              sx={{
                                borderRadius: '8px',
                                height: 36,
                                fontSize: '0.8125rem',
                                /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                              }}
                            >
                              {DEFAULT_DATA_FIELDS.map((f) => (
                                <MenuItem key={f.key} value={f.key} sx={{ fontSize: '0.8125rem' }}>
                                  {f.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={c.operator}
                              onChange={(e) => handleUpdateCondition(c.id, { operator: e.target.value })}
                              sx={{
                                borderRadius: '8px',
                                height: 36,
                                fontSize: '0.8125rem',
                                /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                              }}
                            >
                              {(OPERATORS_BY_CATEGORY[category] || OPERATORS_BY_CATEGORY.string).map((op) => (
                                <MenuItem key={op.value} value={op.value} sx={{ fontSize: '0.8125rem' }}>
                                  {op.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {!isValHidden ? (
                            <TextField
                              size="small"
                              value={c.value}
                              placeholder="Enter filter value..."
                              onChange={(e) => handleUpdateCondition(c.id, { value: e.target.value })}
                              sx={{
                                flex: 1,
                                minWidth: 160,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  height: 36,
                                  fontSize: '0.8125rem',
                                  /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                                },
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 160,
                                height: 36,
                                display: 'flex',
                                alignItems: 'center',
                                px: 1.5,
                                color: 'text.secondary',
                                fontSize: '0.78rem',
                                fontStyle: 'italic',
                              }}
                            >
                              (No value needed)
                            </Box>
                          )}

                          <Tooltip title="Remove condition">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveCondition(c.id)}
                              sx={{
                                borderRadius: '8px',
                                width: 34,
                                height: 34,
                                '&:hover': {
                                  /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}

                {/* Add Next Condition Row */}
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '10px',
                    border: '1px dashed',
                    /** Purpose: Derive the border color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1',
                    /** Purpose: Derive the bgcolor style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.6)',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                  >
                    <Chip
                      label={conditions.length === 0 ? 'Where' : `+ ${matchMode.toUpperCase()}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontWeight: 800,
                        minWidth: 64,
                        textTransform: 'uppercase',
                        fontSize: '0.72rem',
                        height: 28,
                        borderRadius: '6px',
                      }}
                    />

                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        value={newCondField}
                        onChange={(e) => {
                          const nextKey = e.target.value;
                          setNewCondField(nextKey);
                          /** Purpose: Resolve field def for the default data module. Why: The surrounding operation uses this value to make its decision. */
                          const fieldDef = DEFAULT_DATA_FIELDS.find((f) => f.key === nextKey);
                          const ops = OPERATORS_BY_CATEGORY[getCategoryForFieldType(fieldDef?.type)] || OPERATORS_BY_CATEGORY.string;
                          setNewCondOperator(ops[0]?.value || 'equals');
                        }}
                        sx={{
                          borderRadius: '8px',
                          height: 36,
                          fontSize: '0.8125rem',
                          /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                        }}
                      >
                        {DEFAULT_DATA_FIELDS.map((f) => (
                          <MenuItem key={f.key} value={f.key} sx={{ fontSize: '0.8125rem' }}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={newCondOperator}
                        onChange={(e) => setNewCondOperator(e.target.value)}
                        sx={{
                          borderRadius: '8px',
                          height: 36,
                          fontSize: '0.8125rem',
                          /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                        }}
                      >
                        {(OPERATORS_BY_CATEGORY[getCategoryForFieldType(DEFAULT_DATA_FIELDS.find((f) => f.key === newCondField)?.type)] || OPERATORS_BY_CATEGORY.string).map((op) => (
                          <MenuItem key={op.value} value={op.value} sx={{ fontSize: '0.8125rem' }}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {!['is_empty', 'is_not_empty'].includes(newCondOperator) ? (
                      <TextField
                        size="small"
                        placeholder="Enter filter value..."
                        value={newCondValue}
                        onChange={(e) => setNewCondValue(e.target.value)}
                        sx={{
                          flex: 1,
                          minWidth: 160,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            height: 36,
                            fontSize: '0.8125rem',
                            /** Purpose: Derive the background color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                          },
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCondition();
                          }
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 160,
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          px: 1.5,
                          color: 'text.secondary',
                          fontSize: '0.78rem',
                          fontStyle: 'italic',
                        }}
                      >
                        (No value needed)
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={handleAddCondition}
                      sx={{
                        textTransform: 'none',
                        px: 2,
                        height: 36,
                        borderRadius: '8px',
                        fontWeight: 650,
                        fontSize: '0.8125rem',
                        whiteSpace: 'nowrap',
                        boxShadow: 'none',
                      }}
                    >
                      Add Condition
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Collapse>

          {hasActiveFilters && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Active filters:
              </Typography>
              {conditions.length > 1 && (
                <Chip
                  size="small"
                  color="secondary"
                  variant="filled"
                  label={`Logic: ${matchMode.toUpperCase()}`}
                  onClick={() => setMatchMode((prev) => (prev === 'and' ? 'or' : 'and'))}
                  sx={{ fontWeight: 800, cursor: 'pointer', height: 24, fontSize: '0.72rem' }}
                />
              )}
              {search && (
                <Chip
                  size="small"
                  label={`Search: "${search}"`}
                  onDelete={() => {
                    setSearchDraft('');
                    setSearch('');
                  }}
                  color="primary"
                  variant="outlined"
                />
              )}
              {channelFilter !== 'all' && (
                <Chip
                  size="small"
                  label={`Channel: ${channelFilter}`}
                  onDelete={() => setChannelFilter('all')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {statusFilter !== 'all' && (
                <Chip
                  size="small"
                  label={`Status: ${statusFilter}`}
                  onDelete={() => setStatusFilter('all')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {ownerFilter !== 'all' && (
                <Chip
                  size="small"
                  label={`Owner: ${ownerFilter}`}
                  onDelete={() => setOwnerFilter('all')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {conditions.map((c, idx) => {
                /** Purpose: Resolve op obj for the default data module. Why: The surrounding operation uses this value to make its decision. */
                const opObj = (OPERATORS_BY_CATEGORY[c.category] || []).find((o) => o.value === c.operator);
                const opLabel = opObj?.label || c.operator;
                const isValHidden = ['is_empty', 'is_not_empty'].includes(c.operator);
                const prefix = conditions.length > 1 && idx > 0 ? `[${matchMode.toUpperCase()}] ` : '';
                const chipText = `${prefix}${c.fieldLabel} ${opLabel}${isValHidden ? '' : ` "${c.value}"`}`;
                return (
                  <Chip
                    key={c.id}
                    size="small"
                    label={chipText}
                    onDelete={() => handleRemoveCondition(c.id)}
                    color="primary"
                    variant="outlined"
                  />
                );
              })}
              <Button
                size="small"
                startIcon={<FilterAltOffOutlinedIcon />}
                onClick={resetFilters}
                sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.2 }}
              >
                Clear all
              </Button>
            </Stack>
          )}
        </Stack>
      </TableSurface>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <TableSurface sx={{ overflow: 'hidden' }}>
        {loading ? (
          <TableLoadingState rows={7} columns={5} />
        ) : (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 225px)', minHeight: 320 }}>
            <Table stickyHeader size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <TableHeader>
                <TableRow
                  sx={{
                    '& th': {
                      fontWeight: 700,
                      borderRight: '1px solid',
                      borderBottom: '1px solid',
                      /** Purpose: Derive the border color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                    },
                    '& th:last-child': {
                      borderRight: 'none',
                    },
                  }}
                >
                  <TableCell sortDirection={sortField === 'userUid' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortField === 'userUid'}
                      direction={sortField === 'userUid' ? sortOrder : 'asc'}
                      onClick={() => handleSort('userUid')}
                    >
                      User UID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'sessionId' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortField === 'sessionId'}
                      direction={sortField === 'sessionId' ? sortOrder : 'asc'}
                      onClick={() => handleSort('sessionId')}
                    >
                      Session ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'conversationId' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortField === 'conversationId'}
                      direction={sortField === 'conversationId' ? sortOrder : 'asc'}
                      onClick={() => handleSort('conversationId')}
                    >
                      Conversation ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'channel' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortField === 'channel'}
                      direction={sortField === 'channel' ? sortOrder : 'asc'}
                      onClick={() => handleSort('channel')}
                    >
                      Channel
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortField === 'messageCount' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortField === 'messageCount'}
                      direction={sortField === 'messageCount' ? sortOrder : 'asc'}
                      onClick={() => handleSort('messageCount')}
                    >
                      Messages
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'flowId' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortField === 'flowId'}
                      direction={sortField === 'flowId' ? sortOrder : 'asc'}
                      onClick={() => handleSort('flowId')}
                    >
                      Flow
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'owner' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortField === 'owner'}
                      direction={sortField === 'owner' ? sortOrder : 'asc'}
                      onClick={() => handleSort('owner')}
                    >
                      Owner
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'status' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortOrder : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'lastActivityAt' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortField === 'lastActivityAt'}
                      direction={sortField === 'lastActivityAt' ? sortOrder : 'asc'}
                      onClick={() => handleSort('lastActivityAt')}
                    >
                      Last activity
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRecords.map((record) => (
                  <TableRow
                    key={record.conversationId}
                    hover
                    sx={{
                      '& td': {
                        borderRight: '1px solid',
                        borderBottom: '1px solid',
                        /** Purpose: Derive the border color style for the default data view. Why: Theme and state dependent styling should be calculated from current UI context. */
                        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      },
                      '& td:last-child': {
                        borderRight: 'none',
                      },
                    }}
                  >
                    <TableCell>
                      {renderCopyableId(record.userUid)}
                      {record.displayName && record.displayName !== record.userUid ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Name: {record.displayName}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{renderCopyableId(record.sessionId)}</TableCell>
                    <TableCell>{renderCopyableId(record.conversationId)}</TableCell>
                    <TableCell><Chip label={record.channel || 'unknown'} size="small" variant="outlined" /></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{record.messageCount}</TableCell>
                    <TableCell>{renderCopyableId(record.flowId)}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{record.owner}</TableCell>
                    <TableCell><TableStatusBadge label={record.status || 'open'} color={record.status === 'resolved' ? 'success' : 'default'} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(record.lastActivityAt)}</TableCell>
                  </TableRow>
                ))}
                {!sortedRecords.length ? (
                  <TableRow><TableEmptyState asCell colSpan={9} title="No platform records found." /></TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination page={pagination.page} pageCount={pagination.pages} pageSize={pageSize} total={pagination.total} disabled={loading} showRange={false} onPageChange={loadRecords} onPageSizeChange={setPageSize} />
      </TableSurface>
      </>
      )}
    </DefaultTableLayout>
  );
}
